import { filterNullElements } from '@common/adapters/spaDayAdapter';
import { z } from 'zod';

const guestSchema = z.object({
  title: z.string().optional().nullable(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  gender: z.string().optional(),
  externalSpaId: z.union([z.string(), z.number()]).optional().nullable(),
  id: z.union([z.string(), z.number()]).optional(),
});

const priceSchema = z.object({
  amount: z.number(),
  currencyCode: z.string(),
  prefix: z.string().optional(),
  suffix: z.string().optional(),
});

const arrivalTimeSchema = z.object({
  startDateTime: z.string(),
  endDateTime: z.string(),
  price: priceSchema,
  discount: z.any().optional(),
  bandNumber: z.number().optional(),
  numberAvailable: z.number().optional(),
  value: z.string().optional(),
});

type SelectionGroupMeta = {
  id: number;
  required: number;
  treatments: any[];
  classes?: any[];
};

export const buildSpaEntityFormSchema = (selectionGroupsMeta?: SelectionGroupMeta[]) =>
  z
    .object({
      startDate: z.any().optional(),
      visitDate: z.any().optional(),
      arrivalTime: arrivalTimeSchema.optional(),
      guests: z.array(guestSchema),
      notes: z.string().optional(),
      extras: z.record(z.string(), z.any().nullable()).optional(),
      selectionGroups: z
        .record(
          z.string(),
          z.object({
            treatments: z.record(z.string(), z.any()).optional(),
            classes: z.record(z.string(), z.any()).optional(),
          })
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.visitDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'required',
          path: ['visitDate'],
        });
      }

      if (!data.arrivalTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'required',
          path: ['arrivalTime'],
        });
      }

      if (!data.guests || data.guests.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'required',
          path: ['guests'],
        });
        return;
      }

      data.guests.forEach((guest, index) => {
        if (!guest.title?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['guests', index, 'title'],
          });
        }
        if (!guest.firstName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['guests', index, 'firstName'],
          });
        }
        if (!guest.lastName?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['guests', index, 'lastName'],
          });
        }
        if (!guest.gender?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['guests', index, 'gender'],
          });
        }
      });

      if (selectionGroupsMeta && selectionGroupsMeta.length > 0) {
        const numberOfGuests = data.guests.length;

        if (!data.selectionGroups) {
          const selectionId = selectionGroupsMeta[0].id;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'too_few_selections',
            path: ['selectionGroups', `group-${selectionId}`],
          });
          return;
        }

        Object.entries(data.selectionGroups).forEach(([key, groupValues]) => {
          const id = parseInt(key.split('-')[1], 10);
          const group = selectionGroupsMeta.find((g) => g.id === id);
          if (!group) return;

          const { required: requiredItemsPerGuest } = group;
          const treatments = filterNullElements(groupValues.treatments || {});

          const numberOfSelectionsPerGuest = (Object.values(treatments) as any[][])
            .map((selections) => selections.map((s) => s.value?.value || s.value?.id))
            .flat()
            .reduce<Record<string, number>>(
              (acc, userId) => ({
                ...acc,
                [userId]: acc[userId] + 1 || 1,
              }),
              {}
            );

          const isMore =
            numberOfGuests < Object.keys(numberOfSelectionsPerGuest).length ||
            Object.values(numberOfSelectionsPerGuest).find((s) => s > requiredItemsPerGuest);

          const isLess =
            numberOfGuests > Object.keys(numberOfSelectionsPerGuest).length ||
            Object.values(numberOfSelectionsPerGuest).find((s) => s < requiredItemsPerGuest);

          if (isMore) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'too_many_selections',
              path: ['selectionGroups', key],
            });
          } else if (isLess) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'too_few_selections',
              path: ['selectionGroups', key],
            });
          }
        });
      }
    });

export type SpaEntityFormValues = z.infer<ReturnType<typeof buildSpaEntityFormSchema>>;
